// Voucher claim page behaviour: clipboard, the six-box OTP entry, and the inline
// "edit number" toggle. Turbolinks is disabled in this app (Turbolinks.start()
// is commented out in packs/application.js), so every navigation is a full page
// load and DOMContentLoaded is the correct hook -- turbolinks:load never fires.
// Labels come from data attributes rather than a JS i18n lookup, so the copy
// stays translated server-side like the rest of the page.

function flashLabel(button, text) {
  // An icon-only button has no label to swap, and writing textContent would delete
  // the icon for good (the restore puts back a string, not the svg). So flash the
  // glyph to a tick and move the confirmation into the accessible name, which is
  // where a screen reader looks for it anyway.
  const glyph = button.querySelector('svg use');
  if (glyph) {
    const href = glyph.getAttribute('xlink:href') || glyph.getAttribute('href');
    const label = button.getAttribute('aria-label');
    const tick = href.replace(/#.*$/, '#tick');
    glyph.setAttribute('href', tick);
    glyph.setAttribute('xlink:href', tick);
    button.setAttribute('aria-label', text);
    window.setTimeout(() => {
      glyph.setAttribute('href', href);
      glyph.setAttribute('xlink:href', href);
      if (label) button.setAttribute('aria-label', label);
    }, 2000);
    return;
  }

  const original = button.dataset.copyLabel || button.textContent.trim();
  button.textContent = text;
  window.setTimeout(() => {
    button.textContent = original;
  }, 2000);
}

// navigator.clipboard needs a secure context, which msite has, but the
// execCommand path keeps this working in older in-app browsers.
function legacyCopy(text) {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'absolute';
  field.style.left = '-9999px';
  document.body.appendChild(field);
  field.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  }

  document.body.removeChild(field);
  return copied;
}

function bindCopyButtons() {
  document.querySelectorAll('[data-clipboard-text]').forEach((button) => {
    button.addEventListener('click', () => {
      const text = button.dataset.clipboardText;
      if (!text) return;

      // A link also navigates, so only relabel when it is a real button.
      const done = () => {
        if (button.tagName === 'BUTTON') flashLabel(button, button.dataset.copiedLabel || text);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          if (legacyCopy(text)) done();
        });
      } else if (legacyCopy(text)) {
        done();
      }
    });
  });
}

// Six single-character boxes, joined into one hidden field on submit so the
// controller keeps receiving a plain `code` param.
function bindOtpBoxes() {
  const wrap = document.querySelector('[data-vc-otp]');
  if (!wrap) return;

  const boxes = [...wrap.querySelectorAll('input')];
  const hidden = document.querySelector('[data-vc-otp-value]');
  const form = document.querySelector('[data-vc-otp-form]');
  const collect = () => boxes.map((b) => b.value).join('');

  boxes.forEach((box, i) => {
    box.addEventListener('input', () => {
      box.value = box.value.replace(/\D/g, '').slice(0, 1);
      if (box.value && boxes[i + 1]) boxes[i + 1].focus();
      if (hidden) hidden.value = collect();
    });

    box.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !box.value && boxes[i - 1]) boxes[i - 1].focus();
    });

    // Pasting the whole code into any box should fill the row.
    box.addEventListener('paste', (event) => {
      const digits = (event.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
      if (!digits) return;
      event.preventDefault();
      boxes.forEach((b, n) => { b.value = digits[n] || ''; });
      if (hidden) hidden.value = collect();
      (boxes[Math.min(digits.length, boxes.length - 1)] || box).focus();
    });
  });

  if (form && hidden) {
    form.addEventListener('submit', () => { hidden.value = collect(); });
  }
}

// The hint line ends with "Resend in 0:28" (the frame's own copy). Countdown and
// resend button share one slot, so the button replaces the timer in place and the
// line never reflows. Seconds come from the server (mobile_numbers.updated_at),
// so a reload continues the same countdown; the server throttles either way.
function bindResendCountdown() {
  const slot = document.querySelector('[data-vc-resend-slot]');
  const form = document.querySelector('[data-vc-resend-form]');
  if (!slot || !form) return;

  // "Resend in %{time}" — server-translated, interpolated here.
  const template = slot.dataset.vcResendTemplate || '';
  let left = parseInt(slot.dataset.vcResendSeconds, 10);
  if (!Number.isFinite(left)) left = 0;

  const reveal = () => {
    slot.textContent = '';
    form.hidden = false;
    slot.appendChild(form);
  };

  if (left <= 0) {
    reveal();
    return;
  }

  const render = () => {
    const mins = Math.floor(left / 60);
    const secs = String(left % 60).padStart(2, '0');
    slot.textContent = template.replace('%{time}', `${mins}:${secs}`);
  };

  render();
  const tick = window.setInterval(() => {
    left -= 1;
    if (left <= 0) {
      window.clearInterval(tick);
      reveal();
      return;
    }
    render();
  }, 1000);
}

// "Edit" swaps the read-only number for an input, matching the design's
// on-file row.
function bindNumberEdit() {
  const toggle = document.querySelector('[data-vc-edit-number]');
  const editRow = document.querySelector('[data-vc-number-edit]');
  if (!toggle || !editRow) return;

  toggle.addEventListener('click', () => {
    editRow.hidden = false;
    toggle.closest('.vc-onfile').hidden = true;
    const field = editRow.querySelector('input');
    if (field) field.focus();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  bindCopyButtons();
  bindOtpBoxes();
  bindResendCountdown();
  bindNumberEdit();
});
